import { notifications } from "@mantine/notifications"
import { Dispatch, SetStateAction } from "react"
import { useTranslation } from "react-i18next"

import { TraceabilityResponses } from "@tfg/types/endpoints/traceability"

import { queryClient } from "~/backend/queryClient"
import { useUpsertCustomFieldValue } from "~/gen/api"
import { CustomFieldValue } from "~/pages/SupplierPortal/TraceabilityRequests/components/CustomFieldInput"
import { OperationItem } from "~/pages/SupplierPortal/TraceabilityRequests/utils"
import { showAxiosError } from "~/utils/showAxiosError"

export const useUpsertCustomFieldValueHook = ({
  setValue,
  operationItem,
}: {
  setValue: Dispatch<SetStateAction<CustomFieldValue>>
  setCustomFieldValues: Dispatch<
    SetStateAction<TraceabilityResponses.CustomFieldValue[]>
  >
  cfd: TraceabilityResponses.PublicCustomFieldDef
  operationItem: OperationItem
}) => {
  const { t } = useTranslation("traceability")
  const { mutate: upsertCustomFieldValue, isLoading: isUpdating } =
    useUpsertCustomFieldValue({
      mutation: {
        onSuccess: async (data) => {
          setValue((prev) => {
            return {
              ...prev,
              id: data.data,
            }
          })

          notifications.show({
            title: t("customFields.updateCustomField.customField.title"),
            message: t("customFields.updateCustomField.customField.message"),
            color: "green",
          })
          await queryClient.invalidateQueries({
            queryKey: [
              "listTraceabilityRequests",
              operationItem.operation?.informationRequestId,
            ],
            refetchType: "none",
          })
        },
        onError: async (error) => {
          showAxiosError({
            showNotification: true,
            error,
          })
          await queryClient.invalidateQueries({
            queryKey: [
              "listTraceabilityRequests",
              operationItem.operation?.informationRequestId,
            ],
          })
        },
      },
    })
  return {
    upsertCustomFieldValue,
    isUpdating,
  }
}

export const updateCustomFieldValue = (
  prev: TraceabilityResponses.CustomFieldValue[],
  newValue: Omit<TraceabilityResponses.CustomFieldValue, "document">
): TraceabilityResponses.CustomFieldValue[] => {
  const index = prev.findIndex(
    (item) =>
      item.customFieldDefinitionId === newValue.customFieldDefinitionId &&
      item.operationId === newValue.operationId
  )

  if (index !== -1) {
    return prev.map((item, i) =>
      i === index ? { ...item, ...newValue } : item
    )
  }
  return [...prev, { ...newValue, document: null }]
}
